import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import * as Sentry from '@sentry/react';
import { AuthContext } from '@/context/auth-context';
import { signOut } from '@/services/auth/sign-out';
import { ROUTES } from '@/constants/routes';
import { LAST_ACTIVITY_KEY } from '@/constants/session-keys';
import { broadcastLogout, LogoutReason } from '@/utils/logout-broadcast';
import {
  clearResumeLocation,
  rememberResumeLocation,
} from '@/utils/resume-location';

interface LogoutOptions {
  // False only when we're acting on another tab's signal, so the two tabs
  // don't bounce the same logout back and forth.
  broadcast?: boolean;
  reason?: LogoutReason;
}

export function useLogout() {
  const navigate = useNavigate();
  const { resetUser } = useContext(AuthContext);

  return useCallback(
    async ({ broadcast = true, reason = 'user' }: LogoutOptions = {}) => {
      // Read before the teardown below navigates away from it. Only an
      // involuntary sign-out resumes — someone who chose to sign out expects
      // the default page next time.
      if (reason === 'idle') {
        rememberResumeLocation();
      } else {
        clearResumeLocation();
      }

      if (broadcast) {
        broadcastLogout(reason);
      }

      // The backend call is best-effort: a failure here must never abort the
      // teardown below, or a network blip would leave the session — and its
      // refresh token — alive after an idle timeout.
      try {
        const response = await signOut();
        if (response?.detail) {
          Sentry.captureException(new Error(response.detail), {
            extra: { context: 'useLogout: sign-out returned detail' },
          });
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { context: 'useLogout' } });
      }

      resetUser();
      localStorage.removeItem('currentOrg');
      // Leaving this behind would hand the next session an already-spent clock.
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      navigate(ROUTES.LOGIN);
    },
    [navigate, resetUser],
  );
}
